import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { View, Image, Modal, Pressable, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { connect } from 'react-redux';
import Toast from 'react-native-root-toast';
import { useNavigation } from '@react-navigation/native';

import { ErrorCallout, Text } from '../../components';
import { Alert } from '../../components/other/Alert';
import { STATUS, ROLE_PERMISSIONS, POS_TYPE } from '../../../common/constants';
import {
	TICKET_RESOLVE_MUTATION, TICKET_CANT_RESOLVE_MUTATION,
} from '../../../common/graphql/ticket';
import { useUserContext } from '../../../common/utils/context';
import getGraphQLErrorMessage from '../../../common/utils/errors/getGraphQLErrorMessage';
import { getUserName } from '../../../common/utils/user/getUserName';
import { getFullMachineName } from '../../../common/utils/machine';
import {
	getTicketResolutionBlocker,
	hasTicketRepairReport,
	isTicketRepairReportRequired,
} from '../../../common/utils/ticket/canResolveTicket';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { PrimaryButton, SecondaryButton, Tag } from '../../design/components';

import withMediaPicker from '../../hocs/withMediaPicker';
import { ImageViewer } from '../../components/image';
import { app as appActions } from '../../actions';

import { NoteCreatorView, NoteDetailView } from '../note';

import CantResolveDialog from './CantResolveDialog';
import TicketListItemCostsFields from './TicketListItemCostsFields';
import { createTicketListItemStyles } from './TicketListItem.styles';
import TicketRepairReportDialog from './TicketRepairReportDialog';
import useTicketPhotoUploads from './useTicketPhotoUploads';
import { useOfflineQueue } from '../../offline';
import { isMyDayTraining } from '../myDay/myDayTraining';
import { getStatusLabel } from '../../utils/statusLabel';

const getTicketStatusTone = (status, notResolved) => {
	if (notResolved) {
		return 'danger';
	}
	if (status === STATUS.OPEN) {
		return 'warning';
	}
	if (status === STATUS.RESOLVED) {
		return 'success';
	}
	return 'neutral';
};

const TicketListItem = ({
	item,
	openDialog,
	closeDialog,
	captureMedia,
	capturedMediaBatch,
	processingMedia,
	customProps: {
		modifyItem,
		showMachineName,
		isTicketList,
		onTicketResolved,
		trainingContext,
		serviceDocketId,
	} = {},
}) => {
	const { hasPermission, tenantSettings } = useUserContext();
	const { isOnline, queue } = useOfflineQueue();
	const isTraining = isMyDayTraining(trainingContext);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const trainingOffline = isTraining && !isOnline;
	// Ticket-list screens provide the association, while generic screens provide the ticket directly.
	const ticket = item.ticket || item;
	const repairReportRequired = isTicketRepairReportRequired(ticket);
	const repairReportComplete = hasTicketRepairReport(ticket);
	const resolutionBlocker = getTicketResolutionBlocker(ticket, tenantSettings);

	const navigation = useNavigation();
	const goToMachineDetailTabsView = useCallback(() => navigation.navigate('MachineDetailTabView', {
		docketId: serviceDocketId,
		machineId: ticket.machine.id,
		machineName: ticket.machine.name,
		locationId: ticket.machine.location?.id,
		isMarket: ticket.machine?.type === POS_TYPE.MARKET,
	}), [navigation, serviceDocketId, ticket.machine?.id, ticket.machine?.name, ticket.machine?.location?.id, ticket.machine?.type]);

	const alreadyActioned = useMemo(() => ticket.status !== STATUS.OPEN || item.notResolved, [ticket.status, item.notResolved]);

	const [modalImageIndex, setModalImageIndex] = useState(null);
	const styles = useThemedStyles(createTicketListItemStyles);
	const { theme: { ui: { colors } } } = useTheme();

	const canViewMachineDetails = hasPermission(ROLE_PERMISSIONS.VIEW_MACHINE_DETAIL_PAGE);
	const [resolveTicket] = useMutation(TICKET_RESOLVE_MUTATION);
	const [cantResolveTicket] = useMutation(TICKET_CANT_RESOLVE_MUTATION);
	const {
		imageUrls,
		hasPendingPhotoSync,
		hasPersistedImages,
		isCapturingOrSavingPhotos,
		hasPhotoWorkPending,
		captureTicketMedia,
	} = useTicketPhotoUploads({
		ticket,
		captureMedia,
		capturedMediaBatch,
		processingMedia,
		isTraining,
		trainingOffline,
		scopedQueue,
	});

	const onCreateNote = useCallback(() => {
		closeDialog();
	}, [closeDialog]);

	const onClickAddNote = useCallback(() => {
		if (trainingOffline) {
			Toast.show('Reconnect to continue training');
			return;
		}
		openDialog(() => (
			<NoteCreatorView
				onSubmitSuccess={onCreateNote}
				showImportant={false}
				initialValues={{ ticket: ticket.id }}
			/>
		));
	}, [onCreateNote, openDialog, ticket.id, trainingOffline]);

	const onClickAddCosts = useCallback(() => {
		if (trainingOffline) {
			Toast.show('Reconnect to continue training');
			return;
		}
		openDialog(() => <TicketListItemCostsFields item={ticket} modifyItem={modifyItem} />);
	}, [openDialog, ticket, modifyItem, trainingOffline]);

	const onClickRepairReport = useCallback(() => {
		if (!isOnline) {
			Toast.show('Reconnect to complete the repair report');
			return;
		}

		openDialog(() => (
			<TicketRepairReportDialog
				ticket={ticket}
				modifyItem={modifyItem}
				closeDialog={closeDialog}
			/>
		));
	}, [closeDialog, isOnline, modifyItem, openDialog, ticket]);

	const onClickEditNote = useCallback((note) => {
		if (trainingOffline) {
			Toast.show('Reconnect to continue training');
			return;
		}
		openDialog(() => (
			<NoteDetailView
				data={note}
				showImportant={false}
			/>
		));
	}, [openDialog, trainingOffline]);

	// remove the dialog when unmounting
	useEffect(() => {
		return () => {
			closeDialog();
		};
	}, [closeDialog]);

	const resolveCurrentTicket = useCallback(async () => {
		try {
			const result = await resolveTicket({
				variables: {
					id: ticket.id,
					...(isTraining ? {
						adminTraining: true,
						serviceDocketId,
					} : {}),
				},
			});
			const resolvedTicket = result?.data?.resolveTicket;
			if (resolvedTicket?.status !== STATUS.RESOLVED) {
				Toast.show('Unable to resolve ticket');
				return;
			}
			onTicketResolved?.(resolvedTicket.id || ticket.id);
		}
		catch (error) {
			Toast.show(getGraphQLErrorMessage(error, 'Unable to resolve ticket'));
		}
	}, [isTraining, onTicketResolved, resolveTicket, serviceDocketId, ticket.id]);

	const onClickResolve = useCallback(() => {
		Alert.alert(
			'Are you sure?',
			'',
			[
				{ text: 'Cancel', onPress: () => { }, style: 'cancel' },
				{
					text: 'Resolve',
					onPress: resolveCurrentTicket,
				},
			],
			{ cancelable: false },
		);
	}, [resolveCurrentTicket]);

	const onClickCantResolve = useCallback(() => {
		openDialog(() => (
			<CantResolveDialog
				cantResolveTicket={cantResolveTicket}
				ticketListItem={item}
				closeDialog={closeDialog}
				trainingContext={trainingContext}
				serviceDocketId={serviceDocketId}
			/>
		));
	}, [cantResolveTicket, closeDialog, item, openDialog, serviceDocketId, trainingContext]);

	const isRepairReportDisabled = trainingOffline || !isOnline;
	const isResolveDisabled = trainingOffline || hasPhotoWorkPending || Boolean(resolutionBlocker);
	const isAddNoteDisabled = trainingOffline || !hasPermission(ROLE_PERMISSIONS.ADD_EDIT_NOTES);
	const isAddCostsDisabled = trainingOffline || !hasPermission(ROLE_PERMISSIONS.ADD_EDIT_TICKETS);
	const isAddPhotoDisabled = trainingOffline || isCapturingOrSavingPhotos || !hasPermission(ROLE_PERMISSIONS.ADD_EDIT_TICKET_PHOTOS);
	const statusLabel = item.notResolved ? 'Not resolved' : getStatusLabel(ticket.status);
	const statusTone = getTicketStatusTone(ticket.status, item.notResolved);

	return (
		<View style={styles.card}>
			<View style={styles.topRow}>
				<Tag label={statusLabel} tone={statusTone} />
				<Text style={styles.identifier} numberOfLines={1}>{ticket.identifier}</Text>
			</View>
			{
				Boolean(showMachineName && ticket.machine) && (
					<Pressable
						onPress={canViewMachineDetails ? goToMachineDetailTabsView : undefined}
						disabled={!canViewMachineDetails}
						accessibilityRole={canViewMachineDetails ? 'link' : 'text'}
						style={({ pressed }) => [styles.machineRow, pressed && styles.pressed]}
					>
						<Text style={[styles.machineName, canViewMachineDetails && styles.machineNameLink]}>
							{getFullMachineName(ticket.machine, ticket?.machine?.place, ticket?.machine?.location)}
						</Text>
						{canViewMachineDetails && <Feather name="chevron-right" size={18} color={colors.accent} />}
					</Pressable>
				)
			}
			{Boolean(ticket.description) && (
				<Text style={styles.ticketDescription}>{ticket.description}</Text>
			)}
			{Boolean(ticket.creator) && (
				<Text style={styles.secondaryText}>{getUserName(ticket.creator)}</Text>
			)}
			{
				Boolean(ticket.notes?.length) && (
					<View style={styles.notes}>
						{ticket.notes.map(note => (
							<View style={styles.note} key={note.id}>
								<Text style={styles.noteDescription}>{`${note.creator.firstname}: ${note.description}`}</Text>
								<Pressable
									onPress={() => onClickEditNote(note)}
									disabled={trainingOffline}
									accessibilityRole="button"
									accessibilityState={{ disabled: trainingOffline }}
									style={({ pressed }) => [styles.editNote, pressed && styles.pressed]}
								>
									<Text style={[styles.editNoteLabel, trainingOffline && styles.disabledText]}>Edit note</Text>
								</Pressable>
							</View>
						))}
					</View>
				)
			}
			{
				imageUrls.length > 0 && (
					<View style={styles.photoRow}>
						{imageUrls.map((imageUrl, index) => (
							<TouchableOpacity
								key={index}
								onPress={() => setModalImageIndex(index)}
								accessibilityRole="imagebutton"
								accessibilityLabel={`Ticket photo ${index + 1}`}
							>
								<Image
									style={styles.photo}
									source={{ uri: imageUrl }}
								/>
							</TouchableOpacity>
						))}
					</View>
				)
			}
			{
				// Only resolving needs the photo (getTicketResolutionBlocker); Not resolved needs none.
				Boolean(ticket.photoRequired && !hasPersistedImages && alreadyActioned && !item.notResolved) && <PhotoRequiredWarning />
			}
			{
				hasPendingPhotoSync && (
					<Text style={styles.pendingSyncNote}>Uploading when back online</Text>
				)
			}
			{
				!alreadyActioned && repairReportRequired && (
					<SecondaryButton
						label={repairReportComplete ? 'Edit repair report' : 'Complete repair report'}
						variant="tonal"
						onPress={onClickRepairReport}
						disabled={isRepairReportDisabled}
					/>
				)
			}
			{
				!alreadyActioned && Boolean(resolutionBlocker) && (
					<ErrorCallout text={resolutionBlocker} textStyle={styles.resolutionBlockerText} />
				)
			}
			{
				!alreadyActioned && (
					<View style={styles.buttonRow}>
						<PrimaryButton
							label="Resolve"
							size="compact"
							style={styles.gridButton}
							onPress={onClickResolve}
							disabled={isResolveDisabled}
						/>
						{
							Boolean(isTicketList) && (
								<SecondaryButton
									label="Can't resolve"
									style={[styles.gridButton, styles.compactButton]}
									onPress={onClickCantResolve}
									disabled={trainingOffline}
								/>
							)
						}
					</View>
				)
			}
			<View style={styles.buttonGrid}>
				<SecondaryButton
					label="Add note"
					style={[styles.gridButton, styles.compactButton]}
					onPress={onClickAddNote}
					disabled={isAddNoteDisabled}
				/>
				{
					tenantSettings.enableTicketsCostsFields && (
						<SecondaryButton
							label="Add costs"
							style={[styles.gridButton, styles.compactButton]}
							onPress={onClickAddCosts}
							disabled={isAddCostsDisabled}
						/>
					)
				}
				<SecondaryButton
					label="Add photo"
					style={[styles.gridButton, styles.compactButton]}
					onPress={captureTicketMedia}
					disabled={isAddPhotoDisabled}
				/>
			</View>
			{
				imageUrls.length > 0 && (
					<Modal
						visible={modalImageIndex !== null}
						transparent={true}
						onRequestClose={() => setModalImageIndex(null)}
					>
						<ImageViewer
							close={() => setModalImageIndex(null)}
							index={modalImageIndex}
							imageUrls={imageUrls.map(url => ({ url }))}
						/>
					</Modal>
				)
			}
		</View>
	);
};

const PhotoRequiredWarning = () => {
	return (
		<ErrorCallout
			text="Photo required"
		/>
	);
};

const mapDispatchToProps = dispatch => ({
	openDialog: component => dispatch(appActions.openDialog(component)),
	closeDialog: () => dispatch(appActions.closeDialog()),
});

const WrappedTicketListItem = connect(null, mapDispatchToProps)(withMediaPicker(TicketListItem));

const TicketListItemWithPicker = (props) => {
	// Scope multi-select to ticket photo uploads.
	const ticketId = props.item?.ticket?.id || props.item?.id;

	return (
		<WrappedTicketListItem
			key={ticketId}
			allowMultipleSelection={true}
			allowMultipleCapture={true}
			collectCapturedMediaBatch={true}
			fallbackToLocalUri={true}
			{...props}
		/>
	);
};

export default TicketListItemWithPicker;
